import { useTranslation } from 'react-i18next'
import { useCallback, useState } from 'react'
import { FileXlsIcon, FilePdfIcon } from '@phosphor-icons/react'
import { toast } from 'sonner'
import { Button, ButtonProps, Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui'
import { useExportEcommerceModule, ExportEcommerceModuleProps, ExportFormat } from '@/feature/export/ecommerce/api'
import { downloadFile } from '../utils'
import { DialogContent, DialogHeader, DialogTitle, DialogTrigger, Dialog, DialogDescription, DialogFooter, DialogClose } from '@/components/ui/dialog'

type ExportButtonProps = ButtonProps & ExportEcommerceModuleProps

const SUPPORTED_FORMATS = [`xlsx`, `pdf`] as const

const FORMAT_ICON = {
  xlsx: FileXlsIcon,
  pdf: FilePdfIcon
}

export const ExportModuleDialog = (props: ExportButtonProps) => {
  const { financialModelId, options, ...rest } = props
  const { t } = useTranslation(`common`)
  const [format, setFormat] = useState<ExportFormat>(`xlsx`)
  const [isOpen, setIsOpen] = useState(false)

  const { mutateAsync: exportModule, isPending } = useExportEcommerceModule({
    financialModelId,
    options,
    format
  })

  const handleExport = useCallback(async () => {
    setIsOpen(false)
    const exportPromise = async () => {
      const data = await exportModule()
      downloadFile(data, `${options.module}.${format}`)
      return data
    }

    toast.promise(exportPromise, {
      loading: t(`table.export.status.loading.title`, {
        module: options.module,
        format
      }),
      success: () => ({
        message: t(`table.export.status.success.title`, {
          module: options.module,
          format
        }),
        description: t(`table.export.status.success.description`)
      }),
      error: t(`table.export.status.error.title`, {
        module: options.module,
        format
      })
    })
  }, [exportModule, format, options.module, t])
  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button disabled={isPending} {...rest} size='md'>
          {t(`buttons.export`)}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t(`table.export.title`, { module: options.module })}</DialogTitle>
          <DialogDescription>
            {t(`table.export.description`, {
              module: options.module,
              format
            })}
          </DialogDescription>
        </DialogHeader>
        <Select onValueChange={value => setFormat(value as ExportFormat)} value={format}>
          <SelectTrigger>
            <SelectValue>
              <span className={`flex items-center gap-1.5`}>
                {format === `xlsx` ? <FileXlsIcon size={24} /> : <FilePdfIcon size={24} />}
                <span>{t(`table.export.format.${format}`)}</span>
              </span>
            </SelectValue>
          </SelectTrigger>

          <SelectContent>
            <SelectGroup>
              {SUPPORTED_FORMATS.map(option => {
                const Icon = FORMAT_ICON[option]
                return (
                  <SelectItem key={option} value={option.toString()}>
                    <span className={`flex items-center gap-1.5`}>
                      <Icon size={24} />
                      <span>{t(`table.export.format.${option}`)}</span>
                    </span>
                  </SelectItem>
                )
              })}
            </SelectGroup>
          </SelectContent>
        </Select>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant='outline' size='md'>
              {t(`buttons.cancel`)}
            </Button>
          </DialogClose>
          <Button onClick={handleExport} disabled={isPending} size='md'>
            {t(`buttons.export`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
